/**
 * src/api/http.js
 *
 * The one authenticated fetch wrapper every api/* module goes through. It
 * attaches the Bearer access token and — because that token is short-lived
 * (~15 min) — transparently refreshes it on a 401 and retries the request once,
 * so a live session never surfaces an expiry to the user.
 *
 * The refresh is single-flight: many calls racing a just-expired token share one
 * POST /security/refresh rather than stampeding it. The refresh itself rides the
 * httpOnly gaussiq_refresh cookie (credentials:'include'), which JS can't read;
 * a failed refresh means the session is genuinely over, so we clear it and bounce
 * to /login.
 *
 * Deliberately NOT routed through here: the MFA pending-token calls in
 * security.js (pre-session — no access token to refresh) and the S3 presigned
 * upload in knowledge.js (not behind our auth at all).
 */

import { getToken, isAuthenticated, storeAccessToken, clearSession } from './security'

const BASE_URL = import.meta.env.VITE_API_URL
const REFRESH_PATH = '/security/refresh'

// While a refresh is in flight, every 401 awaits this same promise instead of
// firing its own. Reset to null once settled so a later expiry can refresh again.
let refreshInFlight = null

async function doRefresh() {
  try {
    const res = await fetch(`${BASE_URL}${REFRESH_PATH}`, {
      method: 'POST',
      credentials: 'include', // sends + receives the httpOnly refresh cookie
    })
    if (!res.ok) return false
    const data = await res.json().catch(() => ({}))
    if (!data.token) return false
    storeAccessToken(data.token)
    return true
  } catch {
    // Network error — treat as a failed refresh; the caller decides what to do.
    return false
  }
}

function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

/**
 * The session is over (refresh failed). Drop the local session and hard-navigate
 * to /login. A full navigation, rather than a router push, also wipes any stale
 * in-memory page state so nothing renders against a dead session.
 */
function forceLogin() {
  clearSession()
  if (window.location.pathname !== '/login') {
    window.location.assign('/login')
  }
}

/**
 * Authenticated fetch. Attaches the Bearer token + credentials, and on a 401
 * refreshes the access token once and retries. Returns the raw Response — the
 * caller decides how to read it (JSON via authedJson, or .blob() for a report).
 * On an unrecoverable 401 it kicks off the redirect to /login and returns the
 * 401 Response, so the caller's own error path still runs while navigation lands.
 *
 * @param {string} path — API path relative to VITE_API_URL (e.g. '/skills/active')
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function authedFetch(path, options = {}) {
  const send = () => {
    const token = getToken()
    return fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })
  }

  let res = await send()
  if (res.status === 401) {
    if (await refreshAccessToken()) {
      res = await send() // retry once with the freshly-rotated token
    } else {
      forceLogin()
    }
  }
  return res
}

/**
 * Raise the shared API error shape from a non-OK Response: an Error whose
 * message is the backend's `detail` when present, carrying `status` (so callers
 * can branch on 404) and `detail`.
 * @param {Response} res
 */
export async function raiseForStatus(res) {
  let detail
  try { detail = (await res.json())?.detail } catch { /* body wasn't JSON */ }
  // A structured detail ({ message, ... }) keeps a readable message; callers that need
  // the rest of it read err.detail.
  const text = typeof detail === 'object' && detail !== null ? detail.message : detail
  const err = new Error(text || `API error ${res.status}: ${res.statusText}`)
  err.status = res.status
  err.detail = detail
  throw err
}

/**
 * Authenticated JSON call — the common path for the api/* modules. Sends `body`
 * as JSON when given, refreshes-and-retries on 401 (via authedFetch), throws the
 * shared error shape on non-OK, and returns the parsed JSON body.
 * @param {string} method
 * @param {string} path
 * @param {*} [body] — omitted for bodyless GET/POST/DELETE
 */
export async function authedJson(method, path, body) {
  const res = await authedFetch(path, {
    method,
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  })
  if (!res.ok) await raiseForStatus(res)
  return res.json()
}

/**
 * Gate for the protected-route guard (AppLayout). Resolves true if there is a
 * usable session: a still-valid access token passes instantly (no network), an
 * expired one is refreshed against the httpOnly refresh cookie. Resolves false —
 * after clearing the local session — only when the refresh cookie is also gone or
 * dead, i.e. the user really must log in again.
 * @returns {Promise<boolean>}
 */
export async function ensureSession() {
  if (isAuthenticated()) return true
  if (await refreshAccessToken()) return true
  clearSession()
  return false
}
